"use client";

import { useEffect, useRef } from "react";
import { gridMetrics } from "@/components/hero/HeroGrid";

/**
 * Dark pre-footer surface for the closing "bridge" composition: fills the
 * section from the grid row line nearest the anchor's vertical midline down
 * to the section's bottom edge, flush with the dark footer below — so the
 * closing card straddles the seam between the light grid and one continuous
 * dark surface (band + footer), instead of stacking two colored bands.
 * Measures layout positions (offsetTop chain), not getBoundingClientRect, so
 * the card's entrance transform can't skew the seam. md+ only.
 */
export default function BridgeBand({ anchor = "[data-cta-card]" }: { anchor?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const compute = () => {
      const w = document.documentElement.clientWidth;
      const parent = el.parentElement;
      const target = parent?.querySelector<HTMLElement>(anchor);
      if (!parent || !target || w < 768) {
        el.style.display = "none";
        return;
      }
      el.style.display = "";
      const { cell } = gridMetrics(w);
      // Anchor's layout top relative to the section, transform-free.
      let y = 0;
      let n: HTMLElement | null = target;
      while (n && n !== parent) {
        y += n.offsetTop;
        n = n.offsetParent as HTMLElement | null;
      }
      const parentTopDoc = parent.getBoundingClientRect().top + window.scrollY;
      const centerDoc = parentTopDoc + y + target.offsetHeight / 2;
      const topDoc = Math.round(centerDoc / cell) * cell;
      el.style.top = `${topDoc - parentTopDoc}px`;
    };

    compute();
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(compute).catch(() => {});

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("grid-relayout", schedule);
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("grid-relayout", schedule);
      cancelAnimationFrame(raf);
    };
  }, [anchor]);

  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none absolute inset-x-0 bottom-0 bg-slate-900"
    />
  );
}
